import { CaretDown, Check } from '@phosphor-icons/react';
import React, { useCallback, useState } from 'react';
import { ResponsiveContainer, BarChart, XAxis, YAxis, Bar } from 'recharts';
import {
  formatCurrency,
  formatNumberCommas,
} from '../../../../../../../utils/CurrencyUtils';
import { formatDate } from '../../../../../../../utils/DateUtils';
import { useScreenView } from '../../../../../../../utils/ScreenViewContext';
import { MonthlyBudgetMetrics } from '../../../../../../../types/apiTypes';
import { unicodeToEmoji } from '../../../../../../../shared/emojiUnicode';
import { ClipLoader } from 'react-spinners';
import { Tooltip } from 'antd';
import { DemoDataWrapper } from '../../../home/components/DemoDataBadge';
import { visibleBudgetBuckets } from '../../../../../../../utils/budgetBuckets';

interface MonthlyBudgetMetricsWithRemaining extends MonthlyBudgetMetrics {
  Remaining: number;
  Saved: number;
  Spent: number;
}

/**
 * This month's share of a bucket's target — the denominator every bar in this file is a percentage
 * of, and the figure each bar's condition compares against.
 *
 * Reads `monthly_goal` off the payload rather than recomputing `budget_bucket_limit /
 * time_window_in_months`. The backend derives that value in the one place both the customer app
 * and the admin portal read from (rds-utils `computeBudgetItemsForCustomer`), already scaled and
 * zeroed for any admin override, so recomputing it here would be a second definition free to drift
 * from the first — the same failure this file is being fixed for.
 *
 * Returns 0 for a bucket with no usable target. Since the admin controls shipped, that is most
 * often a bucket an admin turned off rather than a customer's own $0 goal. Collapsing a non-finite
 * value to 0 as well means callers need one `> 0` test rather than separately defending against
 * NaN and Infinity.
 */
const monthlyTargetOf = (category: MonthlyBudgetMetrics): number => {
  const target = category?.monthly_goal;
  return Number.isFinite(target) && target > 0 ? target : 0;
};

function BudgetCategories({
  addClass,
  monthlyBudgetMetricsData,
  loadingMonthlyBudgetMetricsData,
  isPreActive,
}: {
  addClass?: string;
  monthlyBudgetMetricsData: MonthlyBudgetMetrics[];
  loadingMonthlyBudgetMetricsData: boolean;
  isPreActive: boolean;
}) {
  const [budgetMap, setBudgetMap] = useState(new Map());
  const { screenView } = useScreenView();
  const updateBudgetMap = (category: string, toggle: boolean) => {
    setBudgetMap((prevMap) => {
      const newMap = new Map(prevMap);
      newMap.set(category, toggle ? !prevMap.get(category) : true); // Toggle or set to true
      return newMap;
    });
  };

  // Buckets with no target AND no balance have nothing to draw. Home's Budgeted Savings widget has
  // always dropped them; sharing the rule is what stops the two views disagreeing about whether a
  // bucket exists. Anything that survives may still have a zero target, so the math below guards.
  const monthlyMetricsWithRemaining: MonthlyBudgetMetricsWithRemaining[] =
    visibleBudgetBuckets(monthlyBudgetMetricsData).map((category) => {
      const monthlyTarget = monthlyTargetOf(category);
      // Without a target there is no percentage to express, so the bars stay empty rather than
      // rendering NaN (0/0) or Infinity (n/0) — which recharts draws as a silently broken bar
      // instead of failing, so the bug would look like a full bucket rather than an error.
      const percentOfTarget = (value: number): number =>
        monthlyTarget > 0 ? (value / monthlyTarget) * 100 : 0;

      return {
        ...category,
        Remaining: percentOfTarget(monthlyTarget - category?.saved),
        Saved: percentOfTarget(category?.saved),
        Spent: percentOfTarget(Math.abs(category?.spent)),
      };
    });

  // Did anything survive the visibility filter? Drives the empty state and hides the column
  // headers, which otherwise sat above nothing.
  const hasVisibleBuckets = monthlyMetricsWithRemaining.length > 0;

  /**
   * Is there a goal that can meaningfully be completed?
   *
   * The banner reads "complete" from `remaining <= 0`, and an empty or wholly targetless list
   * totals to exactly 0 — so the absence of goals rendered identically to the achievement of them.
   * A bucket holding money with no target is still worth showing as a row, which is why this is a
   * separate question from `hasVisibleBuckets`.
   */
  const hasTrackableGoal = monthlyMetricsWithRemaining.some(
    (category) => monthlyTargetOf(category) > 0
  );

  const remainingToSave = monthlyMetricsWithRemaining?.reduce(
    (acc, category) => {
      const monthlyTarget = monthlyTargetOf(category);
      // A bucket with no target has nothing left to save toward. Counting it would add
      // `0 - saved`, i.e. quietly subtract this month's savings from the headline figure.
      if (monthlyTarget === 0) return acc;
      return acc + (monthlyTarget - category?.saved);
    },
    0
  );

  const CustomBarChart = useCallback(
    (category: MonthlyBudgetMetricsWithRemaining) => {
      return (
        <BarChart data={[category]} layout="vertical">
          <XAxis type="number" domain={[0, 100]} hide />
          <YAxis type="category" dataKey="name" hide />

          {/* Saved + Remaining (haven't overspent yet) */}
          {category.saved > 0 &&
            category.saved < monthlyTargetOf(category) &&
            Math.abs(category.spent) <= Math.abs(category.saved) && (
              <>
                <Bar
                  dataKey="Saved"
                  fill="#F5DA93"
                  stackId="a"
                  radius={[10, 0, 0, 10]}
                />
                <Bar
                  dataKey="Remaining"
                  fill="#F4F5F6"
                  stackId="a"
                  radius={[0, 10, 10, 0]}
                />
              </>
            )}

          {/* Goal met. Guarded on a real target: a $0 target satisfies `0 <= saved` with nothing
              saved, which is what drew a disabled bucket as a completed goal. */}
          {monthlyTargetOf(category) > 0 &&
            monthlyTargetOf(category) <= category.saved && (
              <Bar
                dataKey="Saved"
                fill="#7CDEBA"
                stackId="a"
                radius={[10, 10, 10, 10]}
              />
            )}

          {/* Overspent before saving enough */}
          {category.saved > 0 &&
            category.saved < monthlyTargetOf(category) &&
            Math.abs(category.spent) > Math.abs(category.saved) && (
              <Bar
                dataKey="Spent"
                fill="#F2826A"
                stackId="a"
                radius={[10, 10, 10, 10]}
              />
            )}

          {/* No savings yet */}
          {category.saved === 0 &&
            category.budget_bucket_limit > 0 &&
            Math.abs(category.spent) > 0 && (
              <Bar
                dataKey="Spent"
                fill="#F2826A"
                stackId="a"
                radius={[10, 10, 10, 10]}
              />
            )}

          {/* Nothing saved or spent yet */}
          {category.saved === 0 &&
            category.budget_bucket_limit > 0 &&
            category.spent === 0 && (
              <Bar
                dataKey="Remaining"
                fill="#F4F5F6"
                stackId="a"
                radius={[10, 10, 10, 10]}
              />
            )}
        </BarChart>
      );
    },
    []
  );

  /**
   * Every bucket was filtered out — nothing to chart and nothing to total.
   *
   * Home has always had this state; this widget never did, so once the visibility filter could
   * empty the list it drew bare Spent / Saved / Available headers under a banner announcing the
   * month's savings target was complete. Copy matches home's for consistency.
   */
  const NoBudgetGoals = () => (
    <div className="flex h-full min-h-[300px] w-full flex-col items-center justify-center">
      <p className="text-[14px] leading-[20px] text-graysecondary">
        No Savings Goals Found
      </p>
    </div>
  );

  const SavingsBanner = ({ remaining }: { remaining: number }) => {
    const isComplete = remaining <= 0;

    const sign = remaining < 0 ? '-' : '';
    const absFixed = Math.abs(Number(remaining.toFixed(2)));
    const [wholeStr, cents] = absFixed.toFixed(2).split('.');

    return (
      <div className="h-[52px] w-full px-4">
        <div className="flex h-full w-full flex-row items-center justify-between overflow-hidden rounded-[8px] border-[1px] border-[#039F76] bg-[#E7F8F0] p-4 align-middle">
          <div className="flex flex-row items-center space-x-2 align-middle">
            <Check size={20} className="text-[#0A8363]" />
            <p className="text-[14px] text-primary md:text-[16px]">
              {isComplete
                ? "This month's savings target is complete. Nice!"
                : 'Remaining to save this month'}
            </p>
          </div>

          {!isComplete && (
            <p className="text-[16px] font-semibold leading-[28px] text-primary">
              ${sign}
              {formatNumberCommas(Number(wholeStr))}.
              <span className="text-[12px] leading-[28px] text-graysecondary">
                {cents}
              </span>
            </p>
          )}
        </div>
      </div>
    );
  };

  const renderScreenView = (screenView: string) => {
    switch (screenView) {
      case 'desktop':
        return (
          <div
            className={`flex flex-col items-center gap-4 border-[1px] border-bordergray bg-white align-middle ${addClass} h-full min-h-full max-w-[1148px]`}
          >
            <div className="max-h-[71px]flex-row flex h-[71px] min-h-[71px] w-full items-center justify-between border-b-[1px] border-bordergray p-4 align-middle">
              <h2 className="text-[16px] font-semibold leading-[24px] text-primary">
                Monthly Budgeted Savings
              </h2>
              {/* <AccentButtonOutline addClass="rounded-full flex flex-row items-center align-middle space-x-2 py-[6px] px-[12px]">
                <Receipt size={20} />
                <p>Budget Money</p>
              </AccentButtonOutline> */}
            </div>
            {!loadingMonthlyBudgetMetricsData && hasTrackableGoal && (
              <SavingsBanner remaining={remainingToSave} />
            )}

            {!loadingMonthlyBudgetMetricsData ? (
              <div className="flex h-[574px] max-h-[574px] w-full flex-col overflow-y-auto overflow-x-hidden px-4">
                {hasVisibleBuckets && (
                  <div
                    style={{ marginLeft: 'calc(30% + 12px)' }}
                    className="grid w-[70%] grid-cols-3 grid-rows-1 items-end justify-end gap-2 text-[12px] font-[600] text-[#7D8191]"
                  >
                    <p>Spent</p>
                    <p>Saved</p>
                    <p>Available</p>
                  </div>
                )}
                {!hasVisibleBuckets ? (
                  <NoBudgetGoals />
                ) : (
                  monthlyMetricsWithRemaining.map(
                    (category: MonthlyBudgetMetricsWithRemaining, idx) => {
                      return (
                        <div
                          key={idx}
                          className="flex w-full flex-col items-center align-middle"
                        >
                          <div className="flex w-full flex-row items-center justify-between gap-x-6 border-b-[1px] border-[#EAECF0] py-3 align-middle">
                            <div className="flex h-full w-[30%] flex-col">
                              <div className="flex flex-row items-center space-x-1 align-middle">
                                <CaretDown
                                  size={24}
                                  onClick={() =>
                                    updateBudgetMap(category?.category, true)
                                  }
                                  weight="bold"
                                  className={`text-graysecondary hover:cursor-pointer ${budgetMap.get(category?.category) && 'rotate-180'}`}
                                />
                                <h2 className="text-[14px] font-[600] text-primary">
                                  {unicodeToEmoji(category?.emoji_unicode)}{' '}
                                  {category?.category_display_name}
                                </h2>
                              </div>
                              <div className="h-[20px] max-h-[20px]">
                                <ResponsiveContainer width="100%" height="100%">
                                  {CustomBarChart(category)}
                                </ResponsiveContainer>
                              </div>
                            </div>
                            <div className="grid w-[70%] grid-cols-3 grid-rows-1 gap-4 text-[16px] text-[#495057]">
                              <p>{formatCurrency(category?.spent)}</p>
                              <p>{formatCurrency(category?.saved)}</p>
                              <p
                                className={`rounded-[4px] px-[10px] py-[2px] text-center font-[700] ${category?.amount > 0 ? 'bg-[#E7F8F0] text-[#0A8363]' : category?.amount < 0 ? 'bg-[#FBEEEE] text-danger' : 'bg-[#F4F5F6] text-[#7D8191]'}`}
                              >
                                {formatCurrency(category?.amount)}
                              </p>
                            </div>
                          </div>
                          {budgetMap?.get(category?.category) && (
                            <div className="flex h-auto min-h-[80px] w-full flex-row py-3">
                              {/* Transactions List */}
                              {category?.recent_transactions &&
                              category?.recent_transactions?.length > 0 ? (
                                <div className="flex w-[60%] flex-col">
                                  <div className="flex w-full flex-row text-[12px] font-semibold text-[#7D8191]">
                                    <p className="w-1/3">Date</p>
                                    <p className="w-1/3">Description</p>
                                    <p className="w-1/3 pl-2">Amount</p>
                                  </div>
                                  {category?.recent_transactions?.map(
                                    (transaction, index) => (
                                      <TransactionItem
                                        key={index}
                                        date={
                                          new Date(
                                            transaction?.transaction_timestamp ??
                                              ''
                                          )
                                        }
                                        description={
                                          transaction?.description?.length
                                            ? transaction?.description
                                            : (transaction?.merchant_name ?? '')
                                        }
                                        amount={transaction?.amount ?? 0}
                                      />
                                    )
                                  )}
                                </div>
                              ) : (
                                <h2 className="w-[60%] items-center justify-center pt-2 text-center">
                                  No recent transactions.
                                </h2>
                              )}

                              {/* Summary Box */}
                              <div className="flex h-auto w-[40%] flex-grow flex-col justify-between space-y-2 rounded-lg bg-[#F4F5F6] p-2 text-[12px] text-[#7D8191]">
                                <div className="flex justify-between space-x-2">
                                  <p>Left over from last month</p>
                                  <p className="font-semibold text-[#1D213B]">
                                    {formatCurrency(category?.rollover)}
                                  </p>
                                </div>
                                <div className="flex justify-between space-x-2">
                                  <p>Goal each month</p>
                                  <p className="font-semibold text-[#1D213B]">
                                    {formatCurrency(
                                      category?.budget_bucket_limit /
                                        category?.time_window_in_months
                                    )}
                                  </p>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    }
                  )
                )}
              </div>
            ) : (
              <div className="flex h-[485px] min-h-[485px] items-center justify-center">
                <ClipLoader
                  color="#1D213B"
                  loading={loadingMonthlyBudgetMetricsData}
                  size={60}
                  aria-label="Loading Spinner"
                  data-testid="loader"
                  className="flex h-full w-full items-center justify-center"
                />
              </div>
            )}
          </div>
        );
      case 'tablet':
        return (
          <div
            className={`flex flex-col items-center gap-4 border-[1px] border-bordergray bg-white align-middle ${addClass} h-full min-h-full max-w-[1148px]`}
          >
            <div className="max-h-[71px]flex-row flex h-[71px] min-h-[71px] w-full items-center justify-between border-b-[1px] border-bordergray p-4 align-middle">
              <h2 className="text-[16px] font-semibold leading-[24px] text-primary">
                Monthly Budgeted Savings
              </h2>
            </div>
            {!loadingMonthlyBudgetMetricsData && hasTrackableGoal && (
              <SavingsBanner remaining={remainingToSave} />
            )}

            {!loadingMonthlyBudgetMetricsData ? (
              <div className="flex h-[574px] max-h-[574px] w-full flex-col overflow-y-auto overflow-x-hidden px-4">
                {hasVisibleBuckets && (
                  <div
                    style={{ marginLeft: 'calc(30% + 12px)' }}
                    className="grid w-[70%] grid-cols-3 grid-rows-1 items-end justify-end gap-2 text-[12px] font-[600] text-[#7D8191]"
                  >
                    <p>Spent</p>
                    <p>Saved</p>
                    <p>Available</p>
                  </div>
                )}
                {!hasVisibleBuckets ? (
                  <NoBudgetGoals />
                ) : (
                  monthlyMetricsWithRemaining.map(
                    (category: MonthlyBudgetMetricsWithRemaining, idx) => {
                      return (
                        <div className="flex w-full flex-col items-center align-middle">
                          <div className="flex w-full flex-row items-center justify-between gap-x-6 border-b-[1px] border-[#EAECF0] py-3 align-middle">
                            <div className="flex h-full w-[30%] flex-col">
                              <div className="flex flex-row items-center space-x-1 align-middle">
                                <CaretDown
                                  size={24}
                                  onClick={() =>
                                    updateBudgetMap(category?.category, true)
                                  }
                                  weight="bold"
                                  className={`text-graysecondary hover:cursor-pointer ${budgetMap.get(category?.category) && 'rotate-180'}`}
                                />
                                <h2 className="text-[14px] font-[600] text-primary">
                                  {unicodeToEmoji(category?.emoji_unicode)}{' '}
                                  {category?.category_display_name}
                                </h2>
                              </div>
                              <div className="h-[20px] max-h-[20px]">
                                <ResponsiveContainer width="100%" height="100%">
                                  {CustomBarChart(category)}
                                </ResponsiveContainer>
                              </div>
                            </div>
                            <div className="grid w-[70%] grid-cols-3 grid-rows-1 gap-4 text-[16px] text-[#495057]">
                              <p>{formatCurrency(category?.spent)}</p>
                              <p>{formatCurrency(category?.saved)}</p>
                              <p
                                className={`rounded-[4px] px-[10px] py-[2px] text-center font-[700] ${category?.amount > 0 ? 'bg-[#E7F8F0] text-[#0A8363]' : category?.amount < 0 ? 'bg-[#FBEEEE] text-danger' : 'bg-[#F4F5F6] text-[#7D8191]'}`}
                              >
                                {formatCurrency(category?.amount)}
                              </p>
                            </div>
                          </div>
                          {budgetMap?.get(category?.category) && (
                            <div className="flex h-auto min-h-[80px] w-full flex-row py-3">
                              {/* Transactions List */}
                              {category?.recent_transactions &&
                              category?.recent_transactions?.length > 0 ? (
                                <div className="flex w-[60%] flex-col">
                                  <div className="flex w-full flex-row text-[12px] font-semibold text-[#7D8191]">
                                    <p className="w-1/3">Date</p>
                                    <p className="w-1/3">Description</p>
                                    <p className="w-1/3 pl-2">Amount</p>
                                  </div>
                                  {category?.recent_transactions?.map(
                                    (transaction, index) => (
                                      <TransactionItem
                                        key={index}
                                        date={
                                          new Date(
                                            transaction?.transaction_timestamp ??
                                              ''
                                          )
                                        }
                                        description={
                                          transaction?.description?.length
                                            ? transaction?.description
                                            : (transaction?.merchant_name ?? '')
                                        }
                                        amount={transaction?.amount ?? 0}
                                      />
                                    )
                                  )}
                                </div>
                              ) : (
                                <h2 className="w-[60%] items-center justify-center pt-2 text-center">
                                  No recent transactions.
                                </h2>
                              )}

                              {/* Summary Box */}
                              <div className="flex h-auto w-[40%] flex-grow flex-col justify-between space-y-2 rounded-lg bg-[#F4F5F6] p-2 text-[12px] text-[#7D8191]">
                                <div className="flex justify-between space-x-2">
                                  <p>Left over from last month</p>
                                  <p className="font-semibold text-[#1D213B]">
                                    {formatCurrency(category?.rollover)}
                                  </p>
                                </div>
                                <div className="flex justify-between space-x-2">
                                  <p>Goal each month</p>
                                  <p className="font-semibold text-[#1D213B]">
                                    {formatCurrency(
                                      category?.budget_bucket_limit /
                                        category?.time_window_in_months
                                    )}
                                  </p>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    }
                  )
                )}
              </div>
            ) : (
              <div className="flex h-[427px] min-h-[427px] items-center justify-center">
                <ClipLoader
                  color="#1D213B"
                  loading={loadingMonthlyBudgetMetricsData}
                  size={60}
                  aria-label="Loading Spinner"
                  data-testid="loader"
                  className="flex h-full w-full items-center justify-center"
                />
              </div>
            )}
          </div>
        );
      case 'mobile':
        return (
          <div
            className={`flex flex-col items-center gap-4 border-[1px] border-bordergray bg-white align-middle ${addClass} h-full min-h-full max-w-[1148px]`}
          >
            <div className="max-h-[71px]flex-row flex h-[71px] min-h-[71px] w-full items-center justify-between border-b-[1px] border-bordergray p-4 align-middle">
              <h2 className="text-[16px] font-semibold leading-[24px] text-primary">
                Monthly Budgeted Savings
              </h2>
            </div>
            {!loadingMonthlyBudgetMetricsData && hasTrackableGoal && (
              <SavingsBanner remaining={remainingToSave} />
            )}

            {!loadingMonthlyBudgetMetricsData ? (
              <div className="flex h-[574px] max-h-[574px] w-full flex-col overflow-y-auto overflow-x-hidden px-4">
                {hasVisibleBuckets && (
                  <div
                    style={{ marginLeft: 'calc(20% + 16px)' }}
                    className="grid w-[80%] grid-cols-3 grid-rows-1 items-end justify-end text-[12px] font-[600] text-[#7D8191]"
                  >
                    <p>Spent</p>
                    <p>Saved</p>
                    <p>Available</p>
                  </div>
                )}
                {!hasVisibleBuckets ? (
                  <NoBudgetGoals />
                ) : (
                  monthlyMetricsWithRemaining.map(
                    (category: MonthlyBudgetMetricsWithRemaining, idx) => {
                      return (
                        <div className="flex w-full flex-col items-center align-middle">
                          <div className="flex w-full flex-row items-center justify-between gap-x-6 border-b-[1px] border-[#EAECF0] py-3 align-middle">
                            <div className="flex h-full w-[20%] flex-col">
                              <div className="flex flex-row items-center space-x-1 align-middle">
                                <h2 className="text-[12px] font-[600] text-primary">
                                  {unicodeToEmoji(category?.emoji_unicode)}{' '}
                                  {category?.category_display_name}
                                </h2>
                              </div>
                            </div>
                            <div className="grid w-[80%] grid-cols-3 grid-rows-1 gap-2 text-[12px] text-[#495057]">
                              <p>{formatCurrency(category?.spent)}</p>
                              <p>{formatCurrency(category?.saved)}</p>
                              <p
                                className={`rounded-[4px] px-[10px] py-[2px] text-center text-[12px] font-[700] ${category?.amount > 0 ? 'bg-[#E7F8F0] text-[#0A8363]' : category?.amount < 0 ? 'bg-[#FBEEEE] text-danger' : 'bg-[#F4F5F6] text-[#7D8191]'}`}
                              >
                                {formatCurrency(category?.amount)}
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    }
                  )
                )}
              </div>
            ) : (
              <div className="flex h-[427px] min-h-[427px] items-center justify-center">
                <ClipLoader
                  color="#1D213B"
                  loading={loadingMonthlyBudgetMetricsData}
                  size={40}
                  aria-label="Loading Spinner"
                  data-testid="loader"
                  className="flex h-full w-full items-center justify-center"
                />
              </div>
            )}
          </div>
        );
    }
  };
  return (
    <DemoDataWrapper showBadge={isPreActive}>
      {renderScreenView(screenView)}
    </DemoDataWrapper>
  );
}
const TransactionItem = ({
  date,
  description,
  amount,
}: {
  date: Date;
  description: string;
  amount: number;
}) => {
  return (
    <div className="flex justify-between space-x-1 whitespace-nowrap text-[12px]">
      <p className="w-1/3 font-semibold text-[#1D213B]">{formatDate(date)}</p>

      <Tooltip title={description} placement="top" mouseEnterDelay={0.3}>
        <p className="w-1/3 overflow-hidden text-ellipsis text-[#1D213B]">
          {description}
        </p>
      </Tooltip>

      <p className="w-1/3 overflow-hidden text-ellipsis whitespace-nowrap pl-2 font-semibold text-[#1D213B]">
        {formatCurrency(amount)}
      </p>
    </div>
  );
};

export default BudgetCategories;
